import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CatalogueApiService, UserInteractionApiService } from '@angular-workspace/services';
import {
  PaginationConfig,
  TableColumn,
  TableComponent,
  ModalComponent,
  withCellTemplates,
} from '../../shared/components/primeng';
import { FilterField, FilterPanelComponent } from '../../shared/components/filter-panel/filter-panel.component';
import { DateRange } from '../../shared/components/primeng/date-range-picker/date-range-picker.component';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ActionDropdownOverlayController } from '../../shared/utils/action-dropdown-overlay.sync';
import { feedbackListTotal, feedbackRejectionComment } from '../../shared/utils/user-feedback.util';
import { ToasterService } from '../../services/toaster.service';
import { FormatService } from '../../shared/services/format.service';

export type ConsumerFeedbackStatus = 'pending' | 'approved' | 'rejected';

export interface ConsumerFeedbackRow {
  id: string;
  assetId: string;
  assetName: string;
  assetType: 'DATABANK' | 'AI_MODEL' | 'USECASE' | string;
  assetTypeLabel: string;
  consumerName: string;
  organisation: string;
  userFeedback: string;
  rating: string;
  ratingValue: number;
  status: ConsumerFeedbackStatus;
  rejectionComment: string;
  createdAt: string;
  createdAtRaw: string;
  originalData: Record<string, unknown>;
}

@Component({
  selector: 'app-consumer-asset-feedback',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FilterPanelComponent,
    TableComponent,
    ModalComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './asset-feedback.component.html',
  styleUrl: './asset-feedback.component.scss',
})
export class ConsumerAssetFeedbackComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Provider and org-admin reuse this table with the platform-admin review layout. */
  @Input() panel: 'consumer' | 'owner' = 'consumer';

  @ViewChild('feedbackCell', { static: false }) feedbackCellTemplate!: TemplateRef<unknown>;
  @ViewChild('ratingCell', { static: false }) ratingCellTemplate!: TemplateRef<unknown>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<unknown>;
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<unknown>;

  private readonly userInteractionApi = inject(UserInteractionApiService);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly toaster = inject(ToasterService);
  private readonly formatService = inject(FormatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();
  private readonly assetNameCache = new Map<string, string>();

  private readonly consumerColumns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: false },
    { key: 'userFeedback', label: 'Feedback', sortable: false, width: '280px' },
    { key: 'rating', label: 'Rating', sortable: false, width: '140px' },
    { key: 'status', label: 'Status', sortable: false, width: '120px' },
    { key: 'createdAt', label: 'Created At', sortable: false, width: '220px' },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '120px' },
  ];

  private readonly ownerColumns: TableColumn[] = [
    { key: 'assetName', label: 'Asset', sortable: false },
    { key: 'assetTypeLabel', label: 'Asset Type', sortable: false },
    { key: 'organisation', label: 'Organisation', sortable: false },
    { key: 'userFeedback', label: 'User Feedback', sortable: false, width: '280px' },
    { key: 'rating', label: 'Rating', sortable: false, width: '140px' },
    { key: 'status', label: 'Status', sortable: false, width: '120px' },
    { key: 'createdAt', label: 'Created At', sortable: false, width: '220px' },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '120px' },
  ];

  columns: TableColumn[] = this.consumerColumns;

  filterFields: FilterField[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'multiselect',
      placeholder: 'Choose Status',
      options: [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
      ],
      value: [{ label: 'All', value: 'all' }],
      maxSelected: 1,
      showClear: false,
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'dateRange',
      placeholder: 'Choose date range',
      value: null,
    },
  ];

  rows: ConsumerFeedbackRow[] = [];
  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  loading = false;
  emptyMessage = 'You have not submitted feedback for any asset yet.';

  private dateRange: DateRange | null = null;
  private statusFilter: 'all' | ConsumerFeedbackStatus = 'all';
  private isInitialFilterLoad = true;

  showFeedbackModal = false;
  feedbackModalRow: ConsumerFeedbackRow | null = null;

  openDropdownRowIndex: number | null = null;
  dropdownPosition: { top: number; left: number | null; right: number | null } | null = null;

  showEditModal = false;
  isSubmittingEdit = false;
  selectedRating = 0;
  private editBaselineRating = 0;
  private editBaselineFeedback = '';
  hoveredRating = 0;
  ratingFeedback = '';
  ratingFormSubmitted = false;
  private editingRow: ConsumerFeedbackRow | null = null;

  showDeleteConfirm = false;
  deleteLoading = false;
  private deletingRow: ConsumerFeedbackRow | null = null;

  get isOwnerPanel(): boolean {
    return this.panel === 'owner';
  }

  async ngOnInit(): Promise<void> {
    if (this.isOwnerPanel) {
      this.columns = this.ownerColumns;
      this.emptyMessage = 'No feedback found';
    }
    await this.loadFeedback();
    setTimeout(() => {
      this.isInitialFilterLoad = false;
    }, 200);
  }

  ngAfterViewInit(): void {
    const updates: { key: string; cellTemplate: TemplateRef<unknown> }[] = [];
    if (this.feedbackCellTemplate) {
      updates.push({ key: 'userFeedback', cellTemplate: this.feedbackCellTemplate });
    }
    if (this.ratingCellTemplate) {
      updates.push({ key: 'rating', cellTemplate: this.ratingCellTemplate });
    }
    if (this.statusCellTemplate) {
      updates.push({ key: 'status', cellTemplate: this.statusCellTemplate });
    }
    if (this.actionCellTemplate) {
      updates.push({ key: 'action', cellTemplate: this.actionCellTemplate });
    }
    if (updates.length) {
      this.columns = withCellTemplates(this.columns, updates);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.closeActionDropdown();
  }

  onFilterChange(filters: Record<string, unknown>): void {
    if (this.isInitialFilterLoad) {
      return;
    }
    if (filters['dateRange'] !== undefined) {
      this.dateRange = (filters['dateRange'] as DateRange | null) ?? null;
    }
    if (filters['status'] !== undefined) {
      this.statusFilter = this.resolveStatusFilter(filters['status']);
    }
    this.pagination = { ...this.pagination, currentPage: 1 };
    void this.loadFeedback();
  }

  onClearFilters(): void {
    this.dateRange = null;
    this.statusFilter = 'all';
    this.pagination = { ...this.pagination, currentPage: 1 };
    void this.loadFeedback();
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    void this.loadFeedback();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = { ...this.pagination, pageSize, currentPage: 1 };
    void this.loadFeedback();
  }

  get openDropdownRow(): ConsumerFeedbackRow | null {
    if (this.openDropdownRowIndex == null) {
      return null;
    }
    return this.rows[this.openDropdownRowIndex] ?? null;
  }

  isDropdownOpen(rowIndex: number): boolean {
    return this.openDropdownRowIndex === rowIndex;
  }

  canEditOrDelete(row: ConsumerFeedbackRow): boolean {
    return row.status === 'pending' && !!row.id;
  }

  canShowAction(_row: ConsumerFeedbackRow): boolean {
    return true;
  }

  hasFeedbackText(row: ConsumerFeedbackRow): boolean {
    const text = (row.userFeedback || '').trim();
    return text.length > 0 && text !== '—';
  }

  ratingValue(row: ConsumerFeedbackRow | null | undefined): number {
    const numeric = Number(row?.ratingValue ?? row?.rating);
    return Number.isFinite(numeric) ? Math.min(5, Math.max(0, Math.round(numeric))) : 0;
  }

  isStarFilled(row: ConsumerFeedbackRow, star: number): boolean {
    return star <= this.ratingValue(row);
  }

  get feedbackModalRating(): number {
    return this.ratingValue(this.feedbackModalRow);
  }

  feedbackAuthorName(row: ConsumerFeedbackRow): string {
    return this.collapseRepeatedName(row.consumerName) || 'Unknown';
  }

  feedbackAuthorOrg(row: ConsumerFeedbackRow): string {
    const org = (row.organisation || '').trim();
    if (!org || org === 'N/A' || org === this.feedbackAuthorName(row)) {
      return '';
    }
    return org;
  }

  feedbackAuthorInitials(row: ConsumerFeedbackRow): string {
    const source = this.feedbackAuthorName(row);
    const parts = source.split(/[\s@._-]+/).filter((part) => part.length > 0);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return (parts[0] ?? 'U').substring(0, 2).toUpperCase();
  }

  openFeedbackModal(row: ConsumerFeedbackRow, event?: Event): void {
    event?.stopPropagation();
    this.closeActionDropdown();
    this.feedbackModalRow = row;
    this.showFeedbackModal = true;
    this.cdr.markForCheck();
  }

  closeFeedbackModal(): void {
    this.showFeedbackModal = false;
    this.feedbackModalRow = null;
  }

  onFeedbackModalVisibleChange(visible: boolean): void {
    this.showFeedbackModal = visible;
    if (!visible) {
      this.feedbackModalRow = null;
    }
  }

  statusLabel(status: ConsumerFeedbackStatus): string {
    if (status === 'approved') {
      return 'Approved';
    }
    if (status === 'rejected') {
      return 'Rejected';
    }
    return 'Pending';
  }

  statusChipClass(status: ConsumerFeedbackStatus): string {
    if (status === 'approved') {
      return 'status-chip status-chip-granted';
    }
    if (status === 'rejected') {
      return 'status-chip status-chip-rejected';
    }
    return 'status-chip status-chip-pending';
  }

  toggleActionDropdown(event: Event, rowIndex: number): void {
    event.stopPropagation();
    const row = this.rows[rowIndex];
    if (!row || !this.canShowAction(row)) {
      return;
    }
    if (this.openDropdownRowIndex === rowIndex) {
      this.closeActionDropdown();
      return;
    }
    const button = event.currentTarget as HTMLElement;
    this.openDropdownRowIndex = rowIndex;
    this.actionDropdownOverlay.open({
      anchor: button,
      itemCount: this.actionItemCount(row),
      onPosition: (pos) => {
        this.dropdownPosition = { top: pos.top, left: pos.left, right: pos.right };
        this.cdr.markForCheck();
      },
      onAnchorDetached: () => this.closeActionDropdown(),
    });
    this.cdr.detectChanges();
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownRowIndex = null;
    this.dropdownPosition = null;
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (
      this.openDropdownRowIndex !== null &&
      !target.closest('.action-cell-wrapper') &&
      !target.closest('.action-dropdown-overlay')
    ) {
      this.closeActionDropdown();
    }
  }

  handleAction(action: 'edit' | 'delete' | 'view', row: ConsumerFeedbackRow): void {
    this.closeActionDropdown();
    if (action === 'view') {
      this.openFeedbackModal(row);
      return;
    }
    if (!this.canEditOrDelete(row)) {
      return;
    }
    if (action === 'edit') {
      this.openEditModal(row);
      return;
    }
    this.deletingRow = row;
    this.showDeleteConfirm = true;
  }

  get displayedRating(): number {
    return this.hoveredRating || this.selectedRating;
  }

  get showRatingRequiredError(): boolean {
    return this.ratingFormSubmitted && this.selectedRating < 1;
  }

  get canSaveEdit(): boolean {
    return (
      this.selectedRating !== this.editBaselineRating ||
      this.ratingFeedback.trim() !== this.editBaselineFeedback.trim()
    );
  }

  selectRating(value: number): void {
    this.selectedRating = value;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingRow = null;
    this.selectedRating = 0;
    this.hoveredRating = 0;
    this.ratingFeedback = '';
    this.editBaselineRating = 0;
    this.editBaselineFeedback = '';
    this.ratingFormSubmitted = false;
    this.isSubmittingEdit = false;
  }

  onEditModalVisibleChange(visible: boolean): void {
    this.showEditModal = visible;
    if (!visible) {
      this.closeEditModal();
    }
  }

  async submitEdit(): Promise<void> {
    this.ratingFormSubmitted = true;
    const row = this.editingRow;
    if (!row?.assetId || this.selectedRating < 1 || this.isSubmittingEdit || !this.canSaveEdit) {
      return;
    }
    this.isSubmittingEdit = true;
    this.cdr.detectChanges();
    try {
      const comment = this.ratingFeedback.trim().slice(0, 200);
      const existingSubtype = this.readString(row.originalData, ['actionSubtype', 'action_subtype']);
      const payload: {
        assetId: string;
        assetType: string;
        entityRating: number;
        actionSubtype?: string;
        actionSubdata?: Record<string, unknown>;
      } = {
        assetId: row.assetId,
        assetType: row.assetType || 'DATABANK',
        entityRating: this.selectedRating,
      };
      if (comment) {
        payload.actionSubtype = existingSubtype || 'feedback';
        payload.actionSubdata = { comment };
      }

      await this.userInteractionApi.updateUserFeedback(payload);
      this.toaster.success('Feedback updated.');
      this.closeEditModal();
      await this.loadFeedback();
    } catch (error) {
      console.error('Error updating feedback:', error);
    } finally {
      this.isSubmittingEdit = false;
      this.cdr.detectChanges();
    }
  }

  getDeleteMessage(): string {
    const name = this.deletingRow?.assetName || 'this asset';
    return `Delete your feedback for <strong>${name}</strong>? This cannot be undone.`;
  }

  async onDeleteConfirm(): Promise<void> {
    const row = this.deletingRow;
    if (!row?.assetId) {
      this.toaster.error('Unable to delete feedback. Asset id is missing.');
      this.onDeleteCancel();
      return;
    }
    this.deleteLoading = true;
    this.cdr.detectChanges();
    try {
      await this.userInteractionApi.deleteUserFeedback({ assetId: row.assetId });
      this.toaster.success('Feedback deleted.');
      this.onDeleteCancel();
      await this.loadFeedback();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      this.deleteLoading = false;
      this.cdr.detectChanges();
    }
  }

  onDeleteCancel(): void {
    this.showDeleteConfirm = false;
    this.deletingRow = null;
    this.deleteLoading = false;
  }

  private openEditModal(row: ConsumerFeedbackRow): void {
    this.editingRow = row;
    this.selectedRating = row.ratingValue > 0 ? row.ratingValue : 0;
    this.hoveredRating = 0;
    this.ratingFeedback = row.userFeedback === '—' ? '' : row.userFeedback;
    this.editBaselineRating = this.selectedRating;
    this.editBaselineFeedback = this.ratingFeedback;
    this.ratingFormSubmitted = false;
    this.showEditModal = true;
    this.cdr.detectChanges();
  }

  private actionItemCount(row: ConsumerFeedbackRow): number {
    return this.canEditOrDelete(row) ? 3 : 1;
  }

  private async loadFeedback(): Promise<void> {
    this.loading = true;
    this.closeActionDropdown();
    this.closeFeedbackModal();
    try {
      const queryParams: Record<string, unknown> = {
        page: this.pagination.currentPage,
        size: this.pagination.pageSize,
      };
      const range = this.toApiDateRange(this.dateRange);
      if (range) {
        queryParams['time'] = range.time;
        queryParams['endtime'] = range.endtime;
        queryParams['timerel'] = 'between';
      }
      if (this.statusFilter !== 'all') {
        queryParams['feedbackStatus'] = this.statusFilter.toUpperCase();
      }

      const response = await this.userInteractionApi.getMyUserFeedback(queryParams);
      const items = this.extractList(response);
      let mapped = items.map((item) => this.mapRow(item));
      mapped = mapped.filter((row) => this.matchesDateRange(row) && this.matchesStatus(row));
      mapped = await this.enrichAssetNames(mapped);

      // ponytail: a payload bigger than pageSize is paged locally; a larger server total on that payload is ignored.
      const oversized = items.length > this.pagination.pageSize;
      this.rows = mapped;
      this.pagination = {
        ...this.pagination,
        serverSide: !oversized,
        totalItems: oversized ? mapped.length : feedbackListTotal(response, items.length),
      };
    } catch (error) {
      console.error('Error loading consumer feedback:', error);
      this.rows = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private assetTypeLabel(raw: string): string {
    const type = raw.trim().toUpperCase().replace(/[\s_-]/g, '');
    if (type.includes('DATABANK') || type === 'DATASET') return 'Dataset';
    if (type.includes('AIMODEL') || type === 'MODEL') return 'Model';
    if (type.includes('USECASE') || type === 'APP') return 'Use Case';
    return raw.trim() || 'N/A';
  }

  private mapRow(item: unknown): ConsumerFeedbackRow {
    const source = this.asRecord(item);
    const user = this.asRecord(source['user'] ?? source['consumer'] ?? source['createdBy']);
    const asset = this.asRecord(source['asset'] ?? source['item']);
    const userFeedback = this.readUserFeedback(source);
    const status = this.readStatus(source);
    const createdAtRaw = this.readString(source, [
      'ratingCreatedAt',
      'ratingUpdatedAt',
      'createdAt',
      'created_at',
      'updatedAt',
      'updated_at',
      'submittedAt',
    ]);
    const ratingValue = this.readRatingValue(source);

    return {
      id: this.readString(source, ['id', 'feedbackId', '_id']),
      assetId: this.readString(source, ['assetId', 'asset_id', 'itemId']) || this.readString(asset, ['id']),
      assetName:
        this.readString(source, ['assetName', 'asset_name', 'itemName', 'label', 'name']) ||
        this.readString(asset, ['label', 'name', 'assetName', 'title']) ||
        '',
      assetType: this.readString(source, ['assetType', 'asset_type']) || 'DATABANK',
      assetTypeLabel: this.assetTypeLabel(
        this.readString(source, ['assetType', 'asset_type']) ||
          this.readString(asset, ['type', 'assetType']),
      ),
      consumerName: this.readConsumerName(source, user),
      organisation: this.readOrganisation(source, user),
      userFeedback: userFeedback || '—',
      rating: ratingValue != null ? String(ratingValue) : '—',
      ratingValue: ratingValue ?? 0,
      status,
      rejectionComment: feedbackRejectionComment(source, userFeedback, status),
      createdAt: createdAtRaw ? this.formatService.formatDate(createdAtRaw) : '—',
      createdAtRaw,
      originalData: source,
    };
  }

  private async enrichAssetNames(rows: ConsumerFeedbackRow[]): Promise<ConsumerFeedbackRow[]> {
    const missingIds = [...new Set(rows.filter((row) => !row.assetName && row.assetId).map((row) => row.assetId))];
    await Promise.all(missingIds.map((id) => this.resolveAssetName(id)));
    return rows.map((row) => {
      if (row.assetName) {
        return row;
      }
      const resolved = this.assetNameCache.get(row.assetId);
      return { ...row, assetName: resolved || row.assetId || 'N/A' };
    });
  }

  private async resolveAssetName(assetId: string): Promise<void> {
    if (this.assetNameCache.has(assetId)) {
      return;
    }
    try {
      const response = await this.catalogueApi.getItemById(assetId, { auditEnabled: false });
      const record = this.asRecord(response);
      const result = record['result'] ?? record['results'] ?? record['data'] ?? response;
      const item = Array.isArray(result) ? this.asRecord(result[0]) : this.asRecord(result);
      const name =
        this.readString(item, ['name', 'title', 'label', 'assetName']) ||
        this.readString(this.asRecord(item['dataset']), ['name', 'title', 'label']);
      this.assetNameCache.set(assetId, name || assetId);
    } catch {
      this.assetNameCache.set(assetId, assetId);
    }
  }

  private readConsumerName(
    source: Record<string, unknown>,
    user: Record<string, unknown>,
  ): string {
    const name =
      this.readString(user, ['fullName', 'name', 'userName']) ||
      this.readString(source, ['consumerName', 'userName', 'user_name', 'fullName']) ||
      this.readString(source, ['userEmail', 'email']) ||
      this.readString(user, ['email', 'userEmail']);
    return this.collapseRepeatedName(name) || 'N/A';
  }

  private readOrganisation(
    source: Record<string, unknown>,
    user: Record<string, unknown>,
  ): string {
    return (
      this.readString(source, ['orgName', 'organisationName', 'organisation', 'organization']) ||
      this.readString(user, ['orgName', 'organisationName', 'organisation', 'organization']) ||
      'N/A'
    );
  }

  private collapseRepeatedName(value: string): string {
    const text = value.trim();
    const parts = text.split(/\s+/).filter(Boolean);
    if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
      return parts[0];
    }
    return text;
  }

  private matchesDateRange(row: ConsumerFeedbackRow): boolean {
    const start = this.startOfDay(this.dateRange?.startDate ?? null);
    const end = this.endOfDay(this.dateRange?.endDate ?? null);
    if (!start && !end) {
      return true;
    }
    if (!row.createdAtRaw) {
      return false;
    }
    const created = new Date(row.createdAtRaw).getTime();
    if (Number.isNaN(created)) {
      return false;
    }
    if (start && created < start) {
      return false;
    }
    if (end && created > end) {
      return false;
    }
    return true;
  }

  private matchesStatus(row: ConsumerFeedbackRow): boolean {
    return this.statusFilter === 'all' || row.status === this.statusFilter;
  }

  private resolveStatusFilter(status: unknown): 'all' | ConsumerFeedbackStatus {
    const value = Array.isArray(status) ? status[0] : status;
    const raw =
      value && typeof value === 'object' && 'value' in value
        ? String((value as { value?: unknown }).value ?? '')
        : String(value ?? '');
    if (raw === 'pending' || raw === 'approved' || raw === 'rejected') {
      return raw;
    }
    return 'all';
  }

  private toApiDateRange(range: DateRange | null): { time: string; endtime: string } | null {
    if (!range?.startDate || !range?.endDate) {
      return null;
    }
    const start = new Date(range.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(range.endDate);
    end.setHours(23, 59, 59, 0);
    return {
      time: this.toLocalDateTime(start),
      endtime: this.toLocalDateTime(end),
    };
  }

  private toLocalDateTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private startOfDay(date: Date | null): number | null {
    if (!date) {
      return null;
    }
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next.getTime();
  }

  private endOfDay(date: Date | null): number | null {
    if (!date) {
      return null;
    }
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next.getTime();
  }

  private readStatus(source: Record<string, unknown>): ConsumerFeedbackStatus {
    const raw = this.readString(source, [
      'feedbackStatus',
      'status',
      'approvalStatus',
      'requestStatus',
    ]).toLowerCase();
    if (raw === 'approved' || raw === 'granted') {
      return 'approved';
    }
    if (raw === 'rejected' || raw === 'denied') {
      return 'rejected';
    }
    return 'pending';
  }

  private readRatingValue(source: Record<string, unknown>): number | null {
    const value = source['entityRating'] ?? source['rating'] ?? source['stars'];
    if (value == null || value === '') {
      return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private readUserFeedback(source: Record<string, unknown>): string {
    return (
      this.readCommentFromSubdata(source['actionSubdata']) ||
      this.readString(source, ['feedback', 'userFeedback', 'review', 'comment'])
    );
  }

  private readCommentFromSubdata(value: unknown): string {
    if (typeof value === 'string') {
      try {
        return this.readCommentFromSubdata(JSON.parse(value) as unknown);
      } catch {
        return value.trim();
      }
    }
    const record = this.asRecord(value);
    return this.readString(record, ['comment', 'feedback', 'text', 'message']);
  }

  private extractList(response: unknown): unknown[] {
    const record = this.asRecord(response);
    if (Array.isArray(record['result'])) {
      return record['result'];
    }
    const nestedResult = this.asRecord(record['result']);
    if (Array.isArray(nestedResult['data'])) {
      return nestedResult['data'];
    }
    if (Array.isArray(nestedResult['result'])) {
      return nestedResult['result'];
    }
    if (Array.isArray(record['results'])) {
      return record['results'];
    }
    if (Array.isArray(record['data'])) {
      return record['data'];
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  private readString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }
}
